import { singaporeDayBounds } from "../utils/timezone";
import { SUPABASE_KEY, supabaseRestClient } from "./http";

/** The rfq_items columns the quote-history read selects. */
interface QuoteHistoryItemRow {
  rfq_item_id: number;
  item_name: string | null;
  model_number: string | null;
  quantity_requested: number;
  quoted_price: number | null;
  indent_days: number | null;
  cost_price: number | null;
  saved_remark_id: number | null;
  order_index: number | null;
}

/** The rfqs columns the quote-history read selects, with their items. */
interface QuoteHistoryRfqRow {
  rfq_id: number;
  client_name: string | null;
  client_email: string | null;
  quote_sent_at: string | null;
  quote_sent_by: string | null;
  status: string | null;
  rfq_received_date: string;
  rfq_items: QuoteHistoryItemRow[] | null;
}

type QuotedItemRow = QuoteHistoryItemRow & { quoted_price: number };

/** An item with a (non-zero) quoted price. */
const isQuoted = (item: QuoteHistoryItemRow): item is QuotedItemRow => !!item.quoted_price;

// Function to get all quote history with optional filters
export const getAllQuoteHistory = async (filters: {
  customerEmail?: string;
  rfqId?: number;
  startDate?: string;
  endDate?: string;
  keyword?: string;
}) => {
  try {
    // Always use direct Supabase for quote history - create dedicated client
    const supabaseClient = supabaseRestClient();
    
    // Build the query using confirmed existing columns from database schema
    let url = '/rest/v1/rfqs?select=rfq_id,client_name,client_email,quote_sent_at,quote_sent_by,status,rfq_received_date,rfq_items(rfq_item_id,item_name,model_number,quantity_requested,quoted_price,indent_days,cost_price,saved_remark_id,order_index)';
    
    // Only get RFQs with QUOTED status
    url += '&status=eq.QUOTED';
    
    // Add filters if provided
    if (filters.customerEmail?.trim()) {
      // PostgREST's * wildcard: a literal % in a URL is an escape, so
      // "%ad..." would arrive as one decoded byte instead of "%" + "ad".
      const emailFilter = encodeURIComponent(filters.customerEmail.trim());
      url += `&client_email=ilike.*${emailFilter}*`;
    }
    if (filters.rfqId) {
      url += `&rfq_id=eq.${filters.rfqId}`;
    }
    // The dates are Singapore calendar days; quote_sent_at is a timestamptz.
    // Encoded, because a raw "+" in a query string decodes to a space.
    if (filters.startDate) {
      const { start } = singaporeDayBounds(filters.startDate);
      url += `&quote_sent_at=gte.${encodeURIComponent(start)}`;
    }
    if (filters.endDate) {
      const { nextDayStart } = singaporeDayBounds(filters.endDate);
      url += `&quote_sent_at=lt.${encodeURIComponent(nextDayStart)}`;
    }
    
    url += '&order=quote_sent_at.desc';

    const response = await supabaseClient.get<QuoteHistoryRfqRow[] | null>(url, {
      headers: {
        apikey: SUPABASE_KEY,
        Authorization: `Bearer ${SUPABASE_KEY}`,
        Prefer: "return=representation",
        // Override the default 1000 row limit for quote history
        Range: "0-99999999"
      },
    });

    if (response.status !== 200) {
      console.error("Supabase API error:", response.status, response.statusText);
      throw new Error(`Supabase API error: ${response.statusText}`);
    }
    
    const rfqs = response.data || [];
    
    // Filter and transform RFQs that have quoted items
    const history = rfqs
      .filter((rfq) => {
        const rfqItems = rfq.rfq_items;
        const hasItems = Array.isArray(rfqItems) && rfqItems.length > 0;
        const hasQuotedItems = hasItems && rfqItems.some(isQuoted);

        return hasQuotedItems;
      })
      .map((rfq) => {
        const items = rfq.rfq_items || [];
        const quotedItems = items
          .filter(isQuoted)
          .sort((a, b) => (a.order_index ?? a.rfq_item_id) - (b.order_index ?? b.rfq_item_id));
        
        // Apply keyword filter if provided
        let filteredItems = quotedItems;
        if (filters.keyword?.trim()) {
          const keyword = filters.keyword.trim().toLowerCase();
          filteredItems = quotedItems.filter((item) =>
            item.item_name?.toLowerCase().includes(keyword) ||
            item.model_number?.toLowerCase().includes(keyword)
          );
        }
        
        // Skip RFQ if no items match the keyword filter
        if (filters.keyword?.trim() && filteredItems.length === 0) {
          return null;
        }
        
        const totalQuotedAmount = filteredItems.reduce((sum: number, item) => {
          return sum + (item.quoted_price * item.quantity_requested || 0);
        }, 0);
        
        const transformedRfq = {
          rfq_id: rfq.rfq_id,
          customer_name: rfq.client_name || 'Unknown Customer',
          customer_email: rfq.client_email || '',
          ship_name: '', // Not available in current schema, placeholder
          port_name: '', // Not available in current schema, placeholder
          sent_at: rfq.quote_sent_at || rfq.rfq_received_date,
          sent_by: rfq.quote_sent_by || '',
          status: rfq.status || 'UNKNOWN',
          created_at: rfq.rfq_received_date,
          items_count: filteredItems.length,
          total_amount: Math.round(totalQuotedAmount * 100) / 100,
          quote_group_id: rfq.rfq_id.toString(), // Use RFQ ID as group ID since each RFQ is typically one quote
          items: filteredItems.map((item) => ({
            rfq_item_id: item.rfq_item_id,
            item_name: item.item_name || 'Unknown Item',
            model_number: item.model_number || '',
            quantity: item.quantity_requested || 1,
            quoted_price: item.quoted_price || 0,
            total_price: Math.round((item.quoted_price * item.quantity_requested) * 100) / 100,
            indent_days: item.indent_days ?? 0,
            cost_price: item.cost_price || 0,
            order_index: item.order_index
          }))
        };

        return transformedRfq;
      })
      .filter((rfq) => rfq !== null); // Remove null entries from keyword filtering

    return {
      success: true,
      history
    };
  } catch (error) {
    console.error("[DEBUG] Error in getAllQuoteHistory:", error);
    throw error;
  }
};
